import { Dialog } from '@angular/cdk/dialog'
import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { firstValueFrom } from 'rxjs'

import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'

import { DialogService } from '../../../../services/dialog.service'
import { ErrorService } from '../../../../services/error.service'
import { SpinnerService } from '../../../../services/spinner.service'
import { TranslationService } from '../../../../services/translation.service'
import { ApiError } from '../../../../types/apiError'
import { BulkProductShareCsvTemplate } from '../../../../types/productSharingCsvTemplates'
import { ProductSharingCsvTemplateService } from '../../../../services/productSharingCsvTemplate.service'
import { CommonService } from '../../../../services/common.service'

@Component({
  selector: 'app-create-product-sharing-template',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './create-product-sharing-template.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './create-product-sharing-template.component.scss',
})
export class CreateProductSharingTemplateComponent implements OnInit {
  translationService = inject(TranslationService)
  router = inject(Router)
  private fb = inject(FormBuilder)
  private spinnerService = inject(SpinnerService)
  private dialog = inject(Dialog)
  private dialogService = inject(DialogService)
  private errorService = inject(ErrorService)
  private csvTemplateService = inject(ProductSharingCsvTemplateService)
  private route = inject(ActivatedRoute)
  private commonService = inject(CommonService)

  templateForm = this.fb.group({
    templateName: ['', [Validators.required, Validators.maxLength(36)]],
    productIdentifier: [
      this.translationService.getProductIdentifierText(),
      [Validators.required, Validators.maxLength(36)],
    ],
    companyName: [
      this.translationService.getCompanyNameText(),
      [Validators.required, Validators.maxLength(36)],
    ],
    overrideProductSupplier: [
      this.translationService.getOverrideProductSupplierText(),
      [Validators.required, Validators.maxLength(36)],
    ],
    overrideComponentSuppliers: [
      this.translationService.getOverrideComponentSuppliersText(),
      [Validators.required, Validators.maxLength(36)],
    ],
  })

  title = this.translationService.getCreateCSVTemplateText()

  isEditMode = false
  templateId: string | null = null
  templateForEdit?: BulkProductShareCsvTemplate

  ngOnInit(): void {
    this.route.paramMap.subscribe(async (params) => {
      const templateId = params.get('id')
      this.isEditMode = !!templateId

      if (this.isEditMode) {
        this.title = this.translationService.getEditCSVTemplateText()
        await this.setTemplate(templateId!)
      }
    })
  }

  /**
   * Sets the template data for Edit Mode.
   * @param {string} templateId Template id from route params
   * @returns {Promise<void>} Resolves after form fields have been updated.
   */
  async setTemplate(templateId: string): Promise<void> {
    try {
      this.templateId = templateId
      this.templateForEdit = await firstValueFrom(
        this.csvTemplateService.getById(this.templateId),
      )

      this.setFields()
    } catch (err) {
      const apiErr = err as ApiError
      this.spinnerService.stopSpinner()
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Sets UI fields for Edit Mode.
   * @returns {void}
   */
  setFields(): void {
    if (this.templateForEdit) {
      const templateData: Partial<BulkProductShareCsvTemplate> = {
        ...this.templateForEdit,
      }
      this.templateForm.patchValue(templateData)
    }
  }

  /**
   * Creates or updates the template, depending on whether the page is in edit mode.
   * @returns {Promise<void>} Resolves after the operation completes.
   */
  async onSubmit(): Promise<void> {
    try {
      this.spinnerService.startSpinner()

      if (!this.templateForm.valid) {
        this.spinnerService.stopSpinner()
        return
      }

      const template = this.templateForm.value as BulkProductShareCsvTemplate

      const {
        productIdentifier,
        companyName,
        overrideProductSupplier,
        overrideComponentSuppliers,
      } = template

      const aliasesAreUnique = this.commonService.ensureUniqueFieldAliases({
        productIdentifier,
        companyName,
        overrideProductSupplier,
        overrideComponentSuppliers,
      })

      if (!aliasesAreUnique) {
        this.spinnerService.stopSpinner()
        this.dialogService.showDialog(
          this.dialog,
          this.translationService.getProductSharingCSVTemplatesText(),
          this.translationService.getDuplicateFieldMappingAliasText(),
        )
        return
      }

      let successMsg = this.translationService.getCSVTemplateCreatedText()

      if (!this.isEditMode) {
        await firstValueFrom(this.csvTemplateService.create(template))
      } else {
        if (!this.templateId) {
          this.spinnerService.stopSpinner()
          return
        }

        await firstValueFrom(
          this.csvTemplateService.edit(this.templateId, template),
        )
        successMsg = this.translationService.getCSVTemplateUpdatedText()
      }

      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getProductSharingCSVTemplatesText(),
        successMsg,
        undefined,
        this.router,
        'settings',
      )

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()

      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Resets form data.
   * @returns {void}
   */
  onReset(): void {
    if (!this.isEditMode) {
      this.templateForm.reset()
    } else {
      this.setFields()
    }
  }
}
